import {
  signinUser,
  ssoUser,
  refreshToken,
  logoutUser,
  sendOtp,
  verifyOtp,
  validateToken,
  registerInterceptor,
  FetcherInterceptor,
  type ValidateTokenOkResponse,
  type ValidateTokenErrorResponse,
  type SigninUserRequestBody,
  type SigninUserOkResponse,
  type SigninUserErrorResponse,
  type SsoUserRequestBody,
  type SsoUserOkResponse,
  type RefreshTokenRequestBody,
  type RefreshTokenOkResponse,
  type LogoutUserOkResponse,
  type SendOtpOkResponse,
  type SendOtpRequestBody,
  type SendOtpErrorResponse,
  type VerifyOtpRequestBody,
  type VerifyOtpErrorResponse
} from '@goldcast/api/auth';

import { hashOTP } from './utils';

import { getEnv } from '@/constants';
import { EVENT_APP_ORG_ID_KEY } from '@/constants/authKeys';
import { getValueFromLocalstore, saveValueToLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import uuid from '@/services/utils/uuid';

export interface AuthSignInResult {
  signInUserSession: {
    accessToken: {
      jwtToken: string;
    };
    idToken: {
      jwtToken: string;
    };
    refreshToken: {
      token: string;
    };
  };
  challengeParam?: {
    email: string;
  };
}

export type RequestOTPResponse = Required<SendOtpOkResponse>;

interface TokenResponseData {
  access_token: string;
  id_token: string;
  refresh_token: string;
  email?: string;
}

interface LogoutResponseData {
  status?: string;
}

export const GCAUTH_ACCESS_TOKEN_KEY = 'gcauth_access_token';
export const GCAUTH_REFRESH_TOKEN_KEY = 'gcauth_refresh_token';

const DEVICE_ID_KEY = 'gcauth_device_id';

// When an access token is already expired or within this window of expiry, proactively
// refresh it on the next auth check instead of waiting for a 401. On cold start / returning
// after the token expired, the first gated call refreshes here so the parallel calls that
// follow reuse the fresh token instead of each firing their own 401 -> refresh -> retry cycle.
const PROACTIVE_REFRESH_BUFFER_MS = 60_000;

function getDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = uuid();
    saveValueToLocalstore(DEVICE_ID_KEY, deviceId);
  }

  return deviceId;
}

function isRefreshTokenAlreadyUpdated(refreshTokenValue: string, storedRefreshToken: string): boolean {
  if (storedRefreshToken !== refreshTokenValue) {
    return true;
  }
  return false;
}

export function isTokenExpired(jwtToken: string, bufferMs = 0): boolean {
  try {
    const payload = jwtToken?.split('.')[1];
    if (!payload) {
      return true;
    }

    const decoded = JSON.parse(atob(payload));
    const expiryTime = decoded.exp * 1000;
    const now = Date.now();

    if (!expiryTime) {
      return true;
    }
    const isExpired = now >= expiryTime - bufferMs;
    loggerService.log('[GCAuth] Token expiry check', {
      expires: new Date(expiryTime).toISOString(),
      now: new Date(now).toISOString(),
      isExpired
    });
    return isExpired;
  } catch (error) {
    loggerService.error('[GCAuth] Error decoding token', error);
    return true;
  }
}

/**
 * Returns the Goldcast event the access token was issued for (its `gc_event_id` claim),
 * or null when the token is missing, malformed, or not scoped to an event.
 */
export function getTokenEventId(jwtToken?: string | null): string | null {
  try {
    const payload = jwtToken?.split('.')[1];
    if (!payload) {
      return null;
    }
    const decoded = JSON.parse(atob(payload));
    return typeof decoded?.gc_event_id === 'string' && decoded.gc_event_id ? decoded.gc_event_id : null;
  } catch {
    return null;
  }
}

registerInterceptor(FetcherInterceptor.PRE_PROCESS_OPTIONS, ({ options }) => {
  const baseUrl = getEnv('VUE_APP_GCAUTH_ENDPOINT') || '';
  const deviceId = getDeviceId();

  return {
    ...options,
    url: `${baseUrl}${options.url}`,
    headers: {
      ...options.headers,
      deviceId
    }
  };
});

class GCAuthService {
  private static instance: GCAuthService;
  private refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): GCAuthService {
    if (!GCAuthService.instance) {
      GCAuthService.instance = new GCAuthService();
    }
    return GCAuthService.instance;
  }

  private async refreshAccessToken(accessToken: string, refreshTokenValue: string): Promise<AuthSignInResult> {
    const organizationId = getValueFromLocalstore(EVENT_APP_ORG_ID_KEY);

    try {
      const requestPayload: RefreshTokenRequestBody = {
        refresh_token: refreshTokenValue,
        organization_id: organizationId
      };

      loggerService.info('[GCAuth] Attempting token refresh');

      const response: RefreshTokenOkResponse = await refreshToken({
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        body: requestPayload
      });

      if (response.error) {
        throw new Error(response.message || response.error || 'Token refresh failed');
      }

      const data = response.data as TokenResponseData;

      if (!data?.access_token || !data?.id_token || !data?.refresh_token) {
        throw new Error('Token refresh failed - invalid response');
      }

      const authResult: AuthSignInResult = {
        signInUserSession: {
          accessToken: {
            jwtToken: data.access_token
          },
          idToken: {
            jwtToken: data.id_token
          },
          refreshToken: {
            token: data.refresh_token
          }
        }
      };

      loggerService.info('[GCAuth] Token refresh successful');
      return authResult;
    } catch (error) {
      loggerService.error('[GCAuth] Token refresh error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Token refresh service unavailable');
    }
  }

  async getCurrentAccessToken(
    accessToken: string,
    refreshTokenValue: string,
    isUnauthorized: boolean,
    error: string
  ): Promise<{ accessToken: string; refreshToken: string }> {
    // Refresh proactively when the token is already expired (or about to expire) even without
    // a 401, so a single gated call refreshes up front and following parallel calls reuse it.
    if (!isUnauthorized && !isTokenExpired(accessToken, PROACTIVE_REFRESH_BUFFER_MS)) {
      loggerService.info('[GCAuth] Token still valid, using cached token');
      return { accessToken, refreshToken: refreshTokenValue };
    }

    if (this.refreshPromise) {
      loggerService.info('[GCAuth] Token refresh already in progress, waiting');
      return this.refreshPromise;
    }

    const storedAccessToken = getValueFromLocalstore(GCAUTH_ACCESS_TOKEN_KEY);
    const storedRefreshToken = getValueFromLocalstore(GCAUTH_REFRESH_TOKEN_KEY);

    if (!storedAccessToken) {
      throw Error('[GCAuth] Access Token not found.');
    }

    if (!storedRefreshToken) {
      throw Error('[GCAuth] Refresh Token not found.');
    }

    if (isRefreshTokenAlreadyUpdated(refreshTokenValue, storedRefreshToken) && !isTokenExpired(storedAccessToken)) {
      loggerService.info('[GCAuth] Refresh token already updated, skipping refresh call');
      return { accessToken: storedAccessToken, refreshToken: storedRefreshToken };
    }

    loggerService.warn('[GCAuth] Access token expired or unauthorized, attempting token refresh', { error });

    const refreshTokenMethod = async () => {
      const result = await this.refreshAccessToken(storedAccessToken, storedRefreshToken);
      return {
        accessToken: result.signInUserSession.accessToken.jwtToken,
        refreshToken: result.signInUserSession.refreshToken.token
      };
    };

    this.refreshPromise = refreshTokenMethod().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  async signIn(
    identifier: string,
    magicLinkSecret: string,
    eventId: string,
    eventShortId: string
  ): Promise<AuthSignInResult> {
    try {
      const requestPayload: SigninUserRequestBody = {
        identifier,
        magicLinkSecret,
        eventId,
        eventShortId
      };

      loggerService.info('[GCAuth] Attempting sign in');

      const response = (await signinUser({ body: requestPayload })) as SigninUserOkResponse & SigninUserErrorResponse;

      if (response?.error === 'session_exists') {
        loggerService.log('[GCAuth] Session exists - triggering OTP flow');
        throw Object.assign(new Error(response.message || 'Session exists'), {
          statusCode: 409,
          status: 409,
          error: response.error,
          details: response.details
        });
      }

      if (!response.access_token || !response.id_token || !response.refresh_token || !response.email) {
        loggerService.error('[GCAuth] Sign in failed - missing required tokens or email');
        throw new Error('This link is invalid or broken. Please request a new link to continue.');
      }

      const authResult: AuthSignInResult = {
        signInUserSession: {
          accessToken: {
            jwtToken: response.access_token
          },
          idToken: {
            jwtToken: response.id_token
          },
          refreshToken: {
            token: response.refresh_token
          }
        },
        challengeParam: {
          email: response.email
        }
      };

      loggerService.info('[GCAuth] Sign in successful');
      return authResult;
    } catch (error) {
      loggerService.error('[GCAuth] Sign in error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Authentication service unavailable');
    }
  }

  async ssoSignIn(code: string, eventId?: string): Promise<SsoUserOkResponse> {
    try {
      const requestPayload: SsoUserRequestBody = {
        code,
        app: 'event',
        ...(eventId && { event_id: eventId })
      };

      loggerService.info('[GCAuth] Attempting SSO sign in');

      const response: SsoUserOkResponse = await ssoUser({ body: requestPayload });

      if (!response.access_token || !response.id_token || !response.refresh_token || !response.email) {
        loggerService.error('[GCAuth] SSO sign in failed - missing required tokens or email');
        throw new Error('This link is invalid or broken. Please request a new link to continue.');
      }

      loggerService.info('[GCAuth] SSO sign in successful');
      return response;
    } catch (error) {
      loggerService.error('[GCAuth] SSO sign in error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('SSO authentication service unavailable');
    }
  }

  async requestOTP(
    emailId: string,
    eventId: string,
    eventShortId: string,
    magicLinkSecret: string
  ): Promise<RequestOTPResponse> {
    if (!emailId?.includes('@') || !eventId || !eventShortId || !magicLinkSecret) {
      throw new Error('Invalid request parameters');
    }

    try {
      loggerService.info('[GCAuth] Requesting OTP');

      const body: SendOtpRequestBody = {
        email_id: emailId,
        eventId,
        eventShortId: eventShortId.trim(),
        magicLinkSecret
      };
      const response = (await sendOtp({ body })) as SendOtpOkResponse & SendOtpErrorResponse;

      if (response?.error) {
        loggerService.error('[GCAuth] Failed to request OTP', { error: response.error });
        throw Object.assign(new Error(response.message || response.error || 'Failed to request OTP'), {
          error: response.error,
          details: response.details
        });
      }

      if (!response.otp_id) {
        throw new Error('Invalid response from send_otp endpoint');
      }

      return response as RequestOTPResponse;
    } catch (error) {
      loggerService.error('[GCAuth] Request OTP error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Authentication service unavailable');
    }
  }

  async verifyOTP(otp: string, otpId: string): Promise<AuthSignInResult> {
    if (!otp || !otpId) {
      throw new Error('OTP and OTP ID are required');
    }

    try {
      loggerService.info('[GCAuth] Verifying OTP');

      const otpHash = await hashOTP(otp);
      const body: VerifyOtpRequestBody = { otp_hash: otpHash, otp_id: otpId };
      const response = (await verifyOtp({ body })) as TokenResponseData & VerifyOtpErrorResponse;

      if (response?.error) {
        loggerService.error('[GCAuth] Failed to verify OTP', { error: response.error });
        throw Object.assign(new Error(response.message || response.error || 'Failed to verify OTP'), {
          error: response.error,
          details: response.details
        });
      }

      if (!response.access_token || !response.id_token || !response.refresh_token || !response.email) {
        throw new Error('Invalid response from verify_otp endpoint');
      }

      const authResult: AuthSignInResult = {
        signInUserSession: {
          accessToken: {
            jwtToken: response.access_token
          },
          idToken: {
            jwtToken: response.id_token
          },
          refreshToken: {
            token: response.refresh_token
          }
        },
        challengeParam: {
          email: response.email
        }
      };

      loggerService.info('[GCAuth] OTP verification successful');
      return authResult;
    } catch (error) {
      loggerService.error('[GCAuth] Verify OTP error', error);
      throw error instanceof Error ? error : new Error('Failed to verify OTP');
    }
  }

  async validateGCAuthToken(token: string): Promise<ValidateTokenErrorResponse> {
    if (!token) {
      throw new Error('Token is required for validation');
    }

    loggerService.info('[GCAuth] Validating access token');

    const response = (await validateToken({ body: { token } })) as ValidateTokenOkResponse & ValidateTokenErrorResponse;

    return response ?? {};
  }

  async signOut(accessToken: string): Promise<boolean> {
    try {
      loggerService.info('[GCAuth] Attempting logout');

      const response: LogoutUserOkResponse = await logoutUser({
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (response.error) {
        loggerService.error('[GCAuth] Logout failed', { error: response.error });
        throw new Error(response.message || response.error || 'Logout failed');
      }

      const data = response.data as LogoutResponseData;

      if (data?.status === 'logged_out' || response.success) {
        loggerService.info('[GCAuth] Logout successful');
        return true;
      }

      throw new Error('Unexpected logout response');
    } catch (error) {
      loggerService.error('[GCAuth] Logout error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Logout service unavailable');
    }
  }
}

export const GCAuth = GCAuthService.getInstance();
